import {
  Component,
  Prop,
  Vue,
} from 'vue-property-decorator';
import { SupportComponents } from '@/components/supplement-info-fields/components';
import { Form } from '@hb/coffee';
import { UserVo } from '@/vo/user';
import type { SupplementInfoField } from '@/components/supplement-info-fields/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { ProfileFieldEnum } from '../verify-stages/exp-profile/types';

import style from './index.module.scss';

export interface ContactFormRef extends Vue {
  /** 验证联系人的所有信息字段 */
  checkFormValidator: () => Promise<boolean>;
  /** 验证指定-联系人的信息字段 */
  checkAssignFieldValidator: (fieldName: ProfileFieldEnum) => Promise<boolean>;
  /** 获取联系人表单数据 */
  getContactFormData: () => UserVo.ContactInfo;
}

@Component({ name: 'ContactForm' })
export default class ContactForm extends Vue {
  /** ”关系“是否锁定不可变更"配偶"(自营-晋商未婚) */
  @Prop(Boolean) readonly lockSelectedSpouse!: boolean;

  /** ”关系“是否不可以选"配偶"(自营-晋商未婚) */
  @Prop(Boolean) readonly disableSelectSpouse!: boolean;

  @Prop(String) readonly commodityId!: string;

  /** 初始化-联系人信息 */
  @Prop({ type: Object, required: true }) readonly contactItem!: UserVo.ContactInfo;

  /** 联系人组上下文，用于校验重复等 */
  @Prop({ type: Array }) readonly contextOtherContacts!: UserVo.ContactInfo[];

  @Prop(Boolean) readonly showMaskContactName!: boolean | undefined;

  @Prop(Boolean) readonly showMaskContactPhone!: boolean | undefined;

  @Prop(Boolean) readonly validateMaskContactPhone!: boolean;


  /** 联系人-姓名 */
  private renderContactName() {
    const ComponentElement = SupportComponents.getComponent(ProfileFieldEnum.CONTACT_NAME);

    if (ComponentElement) {
      return (
        <ComponentElement
          ref={ProfileFieldEnum.CONTACT_NAME}
          value={this.contactItem[ProfileFieldEnum.CONTACT_NAME]}
          contextOtherContacts={this.contextOtherContacts}
          showMask={this.showMaskContactName}
          lockSelectedSpouse={this.lockSelectedSpouse}
          onChange={(val: string) => this.$emit('change', ProfileFieldEnum.CONTACT_NAME, val, false)}
          onImportName={(val: string, isImport?: boolean) => this.$emit('change', ProfileFieldEnum.CONTACT_NAME, val, isImport)}
          onImportPhone={(phone: string, isImport?: boolean) => {
            this.$emit('change', ProfileFieldEnum.CONTACT_PHONE, phone, isImport);
            // 导入手机号后触发校验
            if (isImport) {
              this.$nextTick(() => {
                this.checkAssignFieldValidator(ProfileFieldEnum.CONTACT_PHONE);
              });
            }
          }}
        />
      );
    }

    return null;
  }

  /** 联系人-手机号 */
  private renderContactPhone() {
    const ComponentElement = SupportComponents.getComponent(ProfileFieldEnum.CONTACT_PHONE);

    if (ComponentElement) {
      return (
        <ComponentElement
          ref={ProfileFieldEnum.CONTACT_PHONE}
          value={this.contactItem[ProfileFieldEnum.CONTACT_PHONE]}
          contextOtherContacts={this.contextOtherContacts}
          showMask={this.showMaskContactPhone}
          validateMask={this.validateMaskContactPhone}
          commodityId={this.commodityId}
          onChange={(val: string) => this.$emit('change', ProfileFieldEnum.CONTACT_PHONE, val, false)}
        />
      );
    }

    return null;
  }

  /** 联系人-关系 */
  private renderContactRelation() {
    const ComponentElement = SupportComponents.getComponent(ProfileFieldEnum.CONTACT_RELATION);

    if (ComponentElement) {
      return (
        <ComponentElement
          ref={ProfileFieldEnum.CONTACT_RELATION}
          value={this.contactItem[ProfileFieldEnum.CONTACT_RELATION]}
          contextOtherContacts={this.contextOtherContacts}
          onChange={(val: string) => this.$emit('change', ProfileFieldEnum.CONTACT_RELATION, val)}
          lockSelectedSpouse={this.lockSelectedSpouse}
          disableSelectSpouse={this.disableSelectSpouse}
        />
      );
    }
    return null;
  }

  public async checkFormValidator(): Promise<boolean> {
    const validateResults = await Promise.all([
      this.checkAssignFieldValidator(ProfileFieldEnum.CONTACT_NAME),
      this.checkAssignFieldValidator(ProfileFieldEnum.CONTACT_PHONE),
      this.checkAssignFieldValidator(ProfileFieldEnum.CONTACT_RELATION),
    ]);

    return validateResults.every((result) => result);
  }

  public async checkAssignFieldValidator(fieldName: ProfileFieldEnum): Promise<boolean> {
    try {
      const $fieldRef = this.$refs[fieldName] as SupplementInfoField;

      if ($fieldRef && typeof $fieldRef.onValidator === 'function' && Boolean(await $fieldRef.onValidator()) === false) {
        return false;
      }
    } catch (err) {
      ubtSendError(`checkAssignFieldValidator error for ${fieldName}`);
    }
    return true;
  }

  public getContactFormData(): UserVo.ContactInfo {
    return this.contactItem || {
      contactName: '',
      contactPhone: '',
      contactRelation: null,
    };
  }

  private render() {
    return (
      <div class={style.contactItem}>
        <div class={style.contactItemTitle}>
          {this.$slots.default}
        </div>
        <Form divider round class={style.contactItemForm}>
          {this.renderContactName()}
          {this.renderContactPhone()}
          {this.renderContactRelation()}
        </Form>
      </div>
    );
  }
}
